import { forwardRef } from "react";

import { SxProps, Theme, Box, useTheme } from "@mui/material";

import { NextLinkProps } from "@/components/NextLink/NextLink";
import { FormIcon, FormIconProps } from "@/forms-design-system/FormIcon/FormIcon";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { mergeSx } from "@/helpers/mergeSx";
import { ArrowDiagonalRight } from "@/icons/outlined";

export type FormLinkBlockProps = Omit<NextLinkProps, "as"> & {
  sx?: SxProps<Theme>;
  components?: {
    icon?: {
      size?: FormIconProps["size"];
      color?: FormIconProps["color"];
      sx?: SxProps<Theme>;
    };
  };
};

// DO NOT USE NEXT LINK HERE
// we want to avoid the next link redirection behavior when only query params are changed, without changing the pathname
export const FormLinkBlock = forwardRef<HTMLAnchorElement, FormLinkBlockProps>(
  ({ children, sx, components, ...rest }) => {
    const theme = useTheme();

    if (!theme.palette.forms || !theme.palette.formsComponents?.linkBlock || !theme.palette.formsComponents?.global) {
      return <InvalidThemeErrorMessage />;
    }

    const linkStyles = mergeSx(
      {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        padding: 1,
        borderRadius: theme.radius(1),
        ...theme.typography.body2,
        color: theme.palette.formsComponents.linkBlock.textColor,
        backgroundColor: theme.palette.formsComponents.linkBlock.backgroundColor,
        outlineColor: "transparent",
        transition: "background-color 200ms ease-out, outline 100ms ease-out",
        boxSizing: "border-box",
        textDecoration: "none",
        "&:hover": {
          backgroundColor: theme.palette.formsComponents.linkBlock.hoverBackgroundColor,
          "& > [data-role='form-link-block-icon']": {
            opacity: 1,
          },
        },
        "&:active, &:focus-visible": {
          outline: `3px solid ${theme.palette.formsComponents.global.focusOutlineColor}`,
        },
      },
      sx
    );

    return (
      <Box component="a" {...rest} sx={linkStyles} href={rest.href as string}>
        {children}
        <FormIcon
          data-role="form-link-block-icon"
          size={components?.icon?.size ?? "small"}
          color={components?.icon?.color ?? theme.palette.formsComponents.linkBlock.iconColor}
          sx={mergeSx({ opacity: 0, transition: "opacity 200ms ease-out" }, components?.icon?.sx)}
        >
          <ArrowDiagonalRight />
        </FormIcon>
      </Box>
    );
  }
);

FormLinkBlock.displayName = "FormLinkBlock";
